import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ApiConstants } from '../../shared/constants/api-constants';
import { HttpClient } from '@angular/common/http';
import { ScopeLookup } from '../../change-control/change-control-library/models/scope-lookup';
import { TrainingStatusReportSearchCriteria } from '../models/training-status-search-criteria';
import { TrainingStatusQueryResult } from '../models/training-status-query-result';
import { TrainingStatusReportLookupDetails } from '../models/training-status-report-lookup';
import { SharedConstants } from '../../shared/constants/shared-constants';
import { ContentTypeEnum, ExportRequest, FormatTypeEnum } from '../../my-space/employees-training-plan/models/export';
import { FilterAndSortDetails, FilterDetails } from '../../shared/models/filter-sort-details';
import { TrainingStatusConstants } from '../constants/training-status-constants';
import { SearchMapperService } from '../../shared/services/search-mapper.service';
import { PaginationState } from '../../shared-components/panel-hub/models/pagination-state';
import { AppliedFilters } from '../../shared-components/panel-hub/models/applied-filters';
import { SortCriteria } from '../../shared-components/panel-hub/models/sort-criteria';
import { BaseSearchRequest } from '../../shared/models/search/base-search-request';

@Injectable({
  providedIn: 'root'
})
export class TrainingStatusService {

  fileData: any;
  exportRequest: ExportRequest = {} as ExportRequest;
  FilterKeyValues: { [key: string]: string[] } = {};

  constructor(
    private httpClient: HttpClient,
    private messageService: MessageService,
    private searchMapper: SearchMapperService
  ) { }

  getLookupData(): Observable<TrainingStatusReportLookupDetails> {
    return this.httpClient.get<TrainingStatusReportLookupDetails>(ApiConstants.GetTrainingStatusReport_lookup_API);
  }

  getTrainingStatusReport(
    request: TrainingStatusReportSearchCriteria,
    paginationState: PaginationState | null,
    filters?: AppliedFilters | null,
    sort?: SortCriteria | null
  ): Observable<TrainingStatusQueryResult> {
    const base: BaseSearchRequest = this.searchMapper.mapToBaseSearchRequest(
      filters,
      sort,
      paginationState
    );

    let payload = {
      eQMSScope: request.eQMSScope,
      assignmentDateRange: request.assignmentDateRange,
      ...base
    };

    return this.httpClient.post<TrainingStatusQueryResult>(
      ApiConstants.GetTrainingStatusReport_API,
      payload
    );
  }

  trainingStatusReportExport(exportRequest: ExportRequest): Observable<any> {
    return this.httpClient.post(ApiConstants.Training_Export_Api, exportRequest);
  }

  exportTrainingStatusReport(
    request: TrainingStatusReportSearchCriteria,
    paginationState: PaginationState | null,
    filters?: AppliedFilters | null,
    sort?: SortCriteria | null
  ): Observable<any> {
    const base: BaseSearchRequest = this.searchMapper.mapToBaseSearchRequest(
      filters,
      sort,
      paginationState
    );

    let payload = {
      eQMSScope: request.eQMSScope,
      assignmentDateRange: request.assignmentDateRange,
      ...base
    };

    // Returns byte array directly (same as Document export)
    return this.httpClient.post<any>(
      ApiConstants.TrainingStatusReport_Export_API,
      payload
    );
  }

  exportToExcel(filterData: FilterAndSortDetails, request: TrainingStatusReportSearchCriteria, selectedeQMSScope: string) {
    // To generate Export Request Data
    this.generateExportRequest(filterData, request);
    
    this.trainingStatusReportExport(this.exportRequest).subscribe((data) => {
      this.fileData = data;
      let byteData = this.fileData;
      if(byteData.excelInBytes != "") {
        const byteCharacters = atob(byteData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      let blob = new Blob([byteArray], { type: 'application / vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      var fileName = SharedConstants.TrainingStatusReport_FileName.replace("{eQMSScope}", selectedeQMSScope)
          .replace("{StartDate}", this.date_TO_String(new Date(request.assignmentDateRange[0])))
          .replace("{EndDate}", this.date_TO_String(new Date(request.assignmentDateRange[1])));
      anchor.download = fileName;
      anchor.href = url;
      anchor.click();
      } else {
        this.messageService.add({
          severity: TrainingStatusConstants.Severity_Error,
          summary: TrainingStatusConstants.ErrorMessage_ForNoTrainingOccurence,
        });
      }      
    });
  }

  private generateExportRequest(filterData:FilterAndSortDetails, request: TrainingStatusReportSearchCriteria){
    this.exportRequest.ContentType = ContentTypeEnum.TrainingStatus;
    this.exportRequest.FormatType = FormatTypeEnum.excel;
    // Filter construction based on drop down selection
    let scopes: string[] = [];
    scopes.push(request.eQMSScope.toString());
    let scopeFilter: FilterDetails = {} as FilterDetails;
    scopeFilter.fieldName = "eQMSScope";
    scopeFilter.fieldValues = scopes;
    let assignmentDateRangeFilter: FilterDetails = {} as FilterDetails;
    assignmentDateRangeFilter.fieldName = "assignmentDateRange";
    assignmentDateRangeFilter.fieldValues = [];
    request.assignmentDateRange.forEach((data) => {
      assignmentDateRangeFilter.fieldValues.push(data);
    })
    filterData.filterDetails.push(scopeFilter)
    filterData.filterDetails.push(assignmentDateRangeFilter)

    this.FilterKeyValues = {}; // removing filter assigned, if any in previous transaction
    filterData.filterDetails.forEach((data: FilterDetails) => {
      this.FilterKeyValues[data.fieldName] = data.fieldValues;
    });
    this.exportRequest.Filters = this.FilterKeyValues; // all the fields included
  }

  date_TO_String(date_Object: Date): string {
    // get the year, month, date, hours, and minutes seprately and append to the string.
    let date_String: string =
      date_Object.getFullYear() +
      "_" +
      (date_Object.getMonth() + 1) +
      "_" +
      +date_Object.getDate();
    return date_String;
  }
}
